import { useCallback, useMemo } from 'react';
import { Column } from 'primereact/column';
import {
	DataTable,
	DataTableDataSelectableParams,
	DataTablePFSEvent
} from 'primereact/datatable';
import { Tooltip } from 'primereact/tooltip';

import { ManualInputProvider } from './context/context';
import Filters from './components/filters/Filters';
import ManualInputField from './components/manualInputField/ManualInputField';
import CompetitorSelect from './components/competitor/CompetitorSelect';

import { ProductDetailsMarketShareListingResponseModel } from '../../models/factMarketShareDatas/ProductDetailsMarketShareListingResponseModel';

import { nameof } from '../../shared/utility';

import styles from './ManualInput.module.scss';
import './ManualInput.scss';

import { FactMarketShareDataSummaryResponseModel } from '../../models/factMarketShareDatas/FactMarketShareDataSummaryResponseModel';
import SalesChartContainer from './components/sales/SalesChartContainer';
import SalesInfoGraphPopup from './components/sales/SalesInfoGraphPopup';
import { classNames } from 'primereact/utils';
import { createPortal } from 'react-dom';
import { useSelectCompetitorsButton } from './hooks/useSelectCompetitorsButton';
import { useManualInputFilters } from './hooks/useManualInputFilters';
import { getPreviousQuarterAndYear } from './utilities/getPreviousQuarterAndYear';
import { useManualInput } from './hooks/useManualInput';
import { useWatch } from 'react-hook-form';
import { useManualInputCookies } from './hooks/useManualInputCookies';
import { HttpService, IHttpService } from '../../services/httpService';
import { HoverTabModel, ITabModel } from '../../shared/hoverModel';

const ManualInput = (): JSX.Element => {
	const httpService: IHttpService = useMemo(() => new HttpService(), []);

	const { setCookie } = useManualInputCookies();

	const {
		competitorSelectActive,
		setCompetitorSelectActive,
		selectButtonRef,
		selectCompetitorsDropdownRef
	} = useSelectCompetitorsButton();

	const {
		filtersDataControl,
		filtersOptionsControl,
		setFiltersData,
		onSelectedCompetitorsFilterUpdate
	} = useManualInputFilters(setCompetitorSelectActive);

	const { control, setValue, fetchProducts, fetchPreviousQuarterData } =
		useManualInput(filtersDataControl);

	const [
		tableMainData,
		tablePreviousQuartersData,
		userHasLeadingBrandRights,
		loadingMainData,
		loadingPreviousQuartersData,
		showPreviousQuarter,
		expandedRows,
		hasSearchResults,
		sortField,
		sortOrder
	] = useWatch({
		control: control,
		name: [
			'tableMainData',
			'tablePreviousQuartersData',
			'userHasLeadingBrandRights',
			'loadingMainData',
			'loadingPreviousQuartersData',
			'showPreviousQuarter',
			'expandedRows',
			'hasSearchResults',
			'sortField',
			'sortOrder'
		]
	});

	const [
		brandFilter,
		yearFilter,
		quarterFilter,
		divisionFilter,
		countryFilter,
		selectedCompetitorsFilter
	] = useWatch({
		control: filtersDataControl,
		name: [
			'brandFilter',
			'yearFilter',
			'quarterFilter',
			'divisionFilter',
			'countryFilter',
			'selectedCompetitorsFilter'
		]
	});

	const [competitorSelectOptions, brandOptions] = useWatch({
		control: filtersOptionsControl,
		name: ['competitorSelectOptions', 'brandOptions']
	});

	const tablePreviousQuarterData = tablePreviousQuartersData.quarterData.find(
		(x) => x.isPrecedingToCurrentQuarter
	);

	const onSort = (event: DataTablePFSEvent) => {
		setValue('sortField', event.sortField);
		setValue('sortOrder', event.sortOrder);
	};

	const onRowExpand = (event: any) => {
		event.originalEvent.originalEvent.target
			.closest('.p-rowgroup-header')
			.classList.remove(styles.collapsed);
	};

	const onRowCollapse = (event: any) => {
		event.originalEvent.originalEvent.target
			.closest('.p-rowgroup-header')
			.classList.add(styles.collapsed);
		fetchProducts(true, true);
	};

	const onRenderSales = (
		rowData: FactMarketShareDataSummaryResponseModel
	) => {
		const index = `sales-chart-container-${rowData.id}`;

		if (loadingPreviousQuartersData) {
			return (
				<div className={styles.loadingSpinner}>
					<div></div>
					<div></div>
					<div></div>
					<div></div>
				</div>
			);
		}

		if (!yearFilter || !quarterFilter) {
			return;
		}

		const { quarter } = getPreviousQuarterAndYear(
			yearFilter,
			quarterFilter
		);

		if (
			!tablePreviousQuartersData ||
			!tablePreviousQuartersData.quarterData
		) {
			return;
		}

		return (
			<SalesChartContainer
				index={index}
				rowData={rowData}
				quarter={quarter}
				tablePreviousQuartersData={tablePreviousQuartersData}
				product={rowData['description']}
				productCategory={rowData['productCategoryDescription']}
			/>
		);
	};

	const onRenderTotalMarketPotential = (
		rowData: ProductDetailsMarketShareListingResponseModel
	) => {
		const userCanEditCell = () => {
			if (rowData.isEditable && userHasLeadingBrandRights) {
				return true;
			} else {
				return false;
			}
		};

		const dataInputTypeId = parseInt(
			process.env.REACT_APP_MARKET_POTENTIAL_DATA_INPUT_TYPE_ID!
		);

		return (
			<ManualInputField
				userCanEdit={userCanEditCell()}
				year={yearFilter.getFullYear()}
				quarter={quarterFilter}
				units={rowData.totalMarketSharePotential}
				dataInputTypeId={dataInputTypeId}
				divisionId={divisionFilter}
				brandId={null}
				countryId={countryFilter}
				productId={rowData.id}
				fetchProducts={fetchProducts}
			/>
		);
	};

	const onRenderLeadingBrand = (
		rowData: ProductDetailsMarketShareListingResponseModel
	) => {
		const dataInputTypeId = parseInt(
			process.env.REACT_APP_LEADING_BRAND_DATA_INPUT_TYPE_ID!
		);
		const brandId = parseInt(process.env.REACT_APP_LEADING_BRAND_ID!);

		const userCanEditCell = () => {
			if (rowData.isEditable && userHasLeadingBrandRights) {
				return true;
			} else {
				return false;
			}
		};

		const units =
			rowData.totalLeadingBrandSales !== null
				? rowData.totalLeadingBrandSales
				: '';

		return (
			<ManualInputField
				userCanEdit={userCanEditCell()}
				year={yearFilter.getFullYear()}
				quarter={quarterFilter}
				units={units}
				dataInputTypeId={dataInputTypeId}
				brandId={brandId}
				countryId={countryFilter}
				productId={rowData.id}
				divisionId={divisionFilter}
				fetchProducts={fetchProducts}
			/>
		);
	};

	const onRenderPreviousQuarterHeader = () => {
		const { quarter, year } = getPreviousQuarterAndYear(
			yearFilter,
			quarterFilter
		);

		return (
			<>
				<p>
					{year} - Q{quarter}
				</p>
				<p>Sales</p>
			</>
		);
	};

	const confirmCompetitors = () => {
		setCompetitorSelectActive(false);
		setCookie('manualInputCompetitors', [
			...selectedCompetitorsFilter.map((x) => x.competitorId)
		]);
		fetchProducts();
	};

	const onRenderCompetitorSelectHeader = () => {
		return (
			<>
				{hasSearchResults && (
					<div
						ref={selectButtonRef}
						className={styles.expand}
						onClick={(e) => {
							setCompetitorSelectActive((prev) => !prev);
						}}
					>
						<button className={styles.expandButton}></button>

						<p className={styles.expandText}>Select Competitors</p>
					</div>
				)}
				{hasSearchResults &&
					competitorSelectActive &&
					createPortal(
						<div
							ref={selectCompetitorsDropdownRef}
							className={styles.competitorSelectDropdown}
						>
							<CompetitorSelect
								competitorSelectOptions={
									competitorSelectOptions
								}
								selectedCompetitors={selectedCompetitorsFilter}
								setSelectedCompetitors={
									onSelectedCompetitorsFilterUpdate
								}
								confirmCompetitors={confirmCompetitors}
							/>
						</div>,
						document.getElementById('root')!
					)}
				<p>&nbsp;</p>
				<p>Brand Portfolio</p>
			</>
		);
	};

	const onRenderOtherBrand = (
		rowData: ProductDetailsMarketShareListingResponseModel
	) => {
		const userCanEditCell = () => {
			return rowData.isEditable;
		};

		let value: any = rowData.countryBrandProductDetails.find(
			(c) => c.brandId === brandFilter
		)?.units;

		if (value == null) {
			value = '';
		}

		const dataInputTypeId = parseInt(
			process.env.REACT_APP_BRAND_DATA_INPUT_TYPE_ID!
		);

		return (
			<ManualInputField
				userCanEdit={userCanEditCell()}
				year={yearFilter.getFullYear()}
				quarter={quarterFilter}
				units={value}
				dataInputTypeId={dataInputTypeId}
				brandId={brandFilter}
				countryId={countryFilter}
				productId={rowData.id}
				divisionId={divisionFilter}
				fetchProducts={fetchProducts}
			/>
		);
	};

	const removeCompetitor = useCallback(
		(competitorToRemoveId: any) => {
			tableMainData.forEach((p) => {
				const targetBrand = p.countryBrandProductDetails.find(
					(b) => b.brandId === competitorToRemoveId
				);

				if (targetBrand !== undefined) {
					const requestJson = JSON.stringify({
						year: yearFilter.getFullYear(),
						quarter: quarterFilter,
						units: 0,
						dataInputTypeId: 4,
						brandId: competitorToRemoveId,
						countryId: countryFilter,
						productDetailIds: p.id
					});

					httpService
						.post(
							'factMarketShareDatas',
							requestJson,
							undefined,
							false
						)
						.catch((e) => { });
				}
			});

			const actualSelectedCompetitors: any =
				selectedCompetitorsFilter.filter(
					(c) => c !== competitorToRemoveId
				);

			setFiltersData(
				'selectedCompetitorsFilter',
				actualSelectedCompetitors
			);
			setCookie('manualInputCompetitors', actualSelectedCompetitors);

			document
				.querySelectorAll<HTMLInputElement>(
					`[data-competitor-id='${competitorToRemoveId}']`
				)
				.forEach((c) => {
					const competitorValue = c.value;
					const competitorsCell = c
						.closest('tr')
						?.querySelector('.competitorsCell p');
					const competitorsCellNewValue =
						parseInt(competitorsCell!.innerHTML) -
						parseInt(competitorValue);

					if (!isNaN(competitorsCellNewValue)) {
						competitorsCell!.innerHTML = String(
							competitorsCellNewValue
						);
					}
				});
		},
		[
			countryFilter,
			httpService,
			quarterFilter,
			selectedCompetitorsFilter,
			setCookie,
			setFiltersData,
			tableMainData,
			yearFilter
		]
	);

	const selectedCompetitorsOptions = competitorSelectOptions.filter((x) => {
		return selectedCompetitorsFilter.some(
			(f) => f.competitorId === x.value
		);
	});

	const selectedCompetitorColumns = useMemo(() => {
		return selectedCompetitorsOptions.map((option, i) => (
			<Column
				sortable
				key={`competitor-column-${option.value}`}
				field={option?.label}
				headerClassName={
					styles.competitorHeader +
					' manual-input__competitor-head-cell'
				}
				bodyClassName='manual-input__competitor-cell'
				header={
					<>
						<p>&nbsp;</p>
						<p>
							{option?.label}
							<button
								type='button'
								onClick={() => {
									removeCompetitor(option.value);
								}}
							></button>
						</p>
					</>
				}
				body={(
					rowData: ProductDetailsMarketShareListingResponseModel
				) => {
					const userCanEditCell = () => {
						return rowData.isEditable;
					};

					let value: number | string =
						rowData.countryBrandProductDetails.find(
							(c) => c.brandId === option?.value
						)?.units ?? '';

					const competitorId = option?.value;
					const dataInputTypeId = parseInt(
						process.env.REACT_APP_COMPETITOR_DATA_INPUT_TYPE_ID!
					);

					return (
						<ManualInputField
							dataCompetitorId={String(competitorId)}
							userCanEdit={userCanEditCell()}
							year={yearFilter.getFullYear()}
							quarter={quarterFilter}
							units={value}
							dataInputTypeId={dataInputTypeId}
							divisionId={divisionFilter}
							brandId={competitorId}
							countryId={countryFilter}
							productId={rowData.id}
							fetchProducts={fetchProducts}
						/>
					);
				}}
			/>
		));
	}, [
		fetchProducts,
		selectedCompetitorsOptions,
		countryFilter,
		divisionFilter,
		quarterFilter,
		yearFilter,
		removeCompetitor
	]);

	return (
		<ManualInputProvider>
			<SalesInfoGraphPopup />

			<Filters
				filtersDataControl={filtersDataControl}
				filtersOptionsControl={filtersOptionsControl}
				runSearch={fetchProducts}
			/>

			{hasSearchResults && (
				<div className={styles.previousQuarterContainer}>
					<input
						type='checkbox'
						id='showPrevQ'
						checked={showPreviousQuarter}
						onChange={() => {
							setValue(
								'showPreviousQuarter',
								!showPreviousQuarter
							);
							!showPreviousQuarter && fetchPreviousQuarterData();
						}}
					/>
					<label htmlFor='showPrevQ'>Show previous quarter</label>
				</div>
			)}

			<DataTable
				id='manual-input-table'
				dataKey='productCategoryDescription'
				className={classNames(styles.dataTable, ' manual-input__table')}
				value={tableMainData}
				responsiveLayout='scroll'
				sortField={sortField}
				sortOrder={sortOrder}
				onSort={onSort}
				sortMode='single'
				rowGroupMode='subheader'
				groupRowsBy='productCategoryDescription'
				rowGroupHeaderTemplate={(
					rowData: ProductDetailsMarketShareListingResponseModel
				) => <span>{rowData.productCategoryDescription}</span>}
				expandableRowGroups
				expandedRows={expandedRows}
				onRowToggle={(e) => setValue('expandedRows', e.data)}
				onRowExpand={onRowExpand}
				onRowCollapse={onRowCollapse}
				loading={loadingMainData}
				scrollable
				isDataSelectable={(e: DataTableDataSelectableParams) =>
					e.data.isEditable
				}
				rowClassName={(
					data: ProductDetailsMarketShareListingResponseModel
				) => {
					if (!data.isEditable) {
						return styles['dataTable__edit-blocked'];
					}
				}}
			>
				<Column
					className='manual-input__products-column'
					sortable
					header={
						<>
							<p>&nbsp;</p>
							<p>Products</p>
						</>
					}
					field={nameof<ProductDetailsMarketShareListingResponseModel>(
						'description'
					)}
					body={(
						rowData: ProductDetailsMarketShareListingResponseModel
					) => {
						const value = rowData.description;
						return (
							<>
								<Tooltip
									target={`product-tooltip-${rowData.id}`}
								/>
								<span
									data-pr-tooltip={value}
									id={`product-tooltip-${rowData.id}`}
									className={'tooltip-span'}
								>
									{value}
								</span>
							</>
						);
					}}
					frozen={true}
				/>

				{showPreviousQuarter && (
					<Column
						header={
							<>
								<p>&nbsp;</p>
								<p>Sales</p>
							</>
						}
						headerClassName={styles.previousColumnHeader}
						bodyClassName={
							styles.previousCell +
							' manual-input__sales-info-cell'
						}
						body={onRenderSales}
						frozen={true}
					/>
				)}

				{showPreviousQuarter && (
					<Column
						header={
							<>
								<p>&nbsp;</p>
								<p>Total Market potential</p>
							</>
						}
						headerClassName={styles.previousColumnHeader}
						bodyClassName={styles.previousCell}
						body={(
							rowData: FactMarketShareDataSummaryResponseModel
						) =>
							loadingPreviousQuartersData ? (
								''
							) : (
								<p>
									{tablePreviousQuarterData?.summary.find(
										(p) => p.id === rowData.id
									)?.totalMarketSharePotential ?? 0}
								</p>
							)
						}
						frozen={true}
					/>
				)}

				{showPreviousQuarter && (
					<Column
						header={onRenderPreviousQuarterHeader}
						headerClassName={styles.previousColumnHeader}
						bodyClassName={styles.previousCell}
						body={(
							rowData: FactMarketShareDataSummaryResponseModel
						) =>
							loadingPreviousQuartersData ? (
								''
							) : (
								<p>
									{
										tablePreviousQuarterData?.summary.find(
											(p) => p.id === rowData.id
										)?.totalSales
									}
								</p>
							)
						}
						frozen={true}
					/>
				)}

				{showPreviousQuarter && (
					<Column
						header={
							<>
								<p>&nbsp;</p>
								<p>Market share %</p>
							</>
						}
						headerClassName={styles.previousColumnHeader}
						bodyClassName={styles.previousCell}
						body={(
							rowData: ProductDetailsMarketShareListingResponseModel
						) =>
							loadingPreviousQuartersData ? (
								''
							) : (
								<p>
									{tablePreviousQuarterData?.summary
										.find((p) => p.id === rowData.id)
										?.marketSharePercent?.toFixed(0) ?? 0}
									&nbsp;%
								</p>
							)
						}
						frozen={true}
					/>
				)}

				{showPreviousQuarter && (
					<Column
						header={
							<>
								<p>&nbsp;</p>
								<p>Competitor sales</p>
							</>
						}
						headerClassName={styles.previousColumnHeader}
						bodyClassName={styles.previousCell}
						body={(rowData: FactMarketShareDataSummaryResponseModel) => {
							const productCategoryId = Number(`${rowData.id}${rowData.productCategoryId}`);
							let lbl = '';
							const tabs: ITabModel[] = tablePreviousQuartersData.quarterData.map(d => {
								const currentQuarterProductData = d.summary.find(
									(s) => Number(`${s.id}${s.productCategoryId}`) === productCategoryId
								);
								lbl = d.isPrecedingToCurrentQuarter ? String(currentQuarterProductData?.competitorsTotalSales ?? '') : lbl;
								return {
									header: `${d.quarter} - Q${d.year}`,
									isActive: d.isPrecedingToCurrentQuarter,
									data: (
										<>
											<p>Competitors Total Sales:{' '}
												{currentQuarterProductData?.competitorsTotalSales}
											</p>
											<ul>
												{currentQuarterProductData?.competitorsSales.map(pcs => {
													return <li> {pcs.key} - {' '} {pcs.value}</li>
												})}
											</ul>
										</>
									)
								}
							})
							return HoverTabModel({
								label: lbl,
								tabs: tabs
							})
						}}
						frozen={true}
					/>
				)}

				<Column
					sortable
					header={
						<>
							<p>&nbsp;</p>
							<p>Total Market potential</p>
						</>
					}
					headerClassName='manual-input__total-market-share-potential-head-cell'
					bodyClassName={
						styles.inputCell +
						' ' +
						'manual-input__total-market-share-potential-cell'
					}
					body={loadingMainData ? '' : onRenderTotalMarketPotential}
					field={nameof<ProductDetailsMarketShareListingResponseModel>(
						'totalMarketSharePotential'
					)}
					frozen={true}
				/>

				<Column
					sortable
					header={
						<>
							<p>&nbsp;</p>
							<p>Remaining sales</p>
						</>
					}
					body={(
						rowData: ProductDetailsMarketShareListingResponseModel
					) => {
						const value = rowData.remainingSales;
						return (
							<>
								<p
									className={
										value >= 0 ? 'positive' : 'negative'
									}
								>
									{value}
								</p>
								<Tooltip target='.tooltip-span' />
								<span
									className='tooltip-span'
									data-pr-tooltip={
										value > 0
											? 'Value added is a positive number. '
											: 'Value added is 0 or a negative number. Please enter a positive value.'
									}
								></span>
							</>
						);
					}}
					field={nameof<ProductDetailsMarketShareListingResponseModel>(
						'remainingSales'
					)}
					headerClassName='manual-input__remaining-sales-head-cell'
					bodyClassName={
						styles.inputCell +
						' manual-input__remaining-sales-cell remainingSalesCell'
					}
					frozen={true}
				/>

				<Column
					sortable
					header={
						<>
							{showPreviousQuarter ? (
								<>
									<p>
										{yearFilter.getFullYear()} - Q
										{quarterFilter}
									</p>
									<p>Atlas Copco</p>
								</>
							) : (
								<>
									<p>&nbsp;</p>
									<p>Atlas Copco</p>
								</>
							)}
						</>
					}
					className='totalLeadingBrandCell'
					headerClassName={styles.atlasCopcoHeadCell}
					bodyClassName={styles.atlasCopcoCell}
					body={loadingMainData ? '' : onRenderLeadingBrand}
					field={nameof<ProductDetailsMarketShareListingResponseModel>(
						'totalLeadingBrandSales'
					)}
				/>

				{brandFilter !==
					parseInt(process.env.REACT_APP_LEADING_BRAND_ID!) && (
						<Column
							sortable
							header={
								<>
									<p>&nbsp;</p>
									<p>
										{
											brandOptions.find(
												(b) => b.value === brandFilter
											)?.label
										}
									</p>
								</>
							}
							headerClassName={
								styles.otherBrandsColumnHeader +
								' manual-input__competitors-head-cell'
							}
							bodyClassName={'manual-input__competitors-cell'}
							body={loadingMainData ? '' : onRenderOtherBrand}
						/>
					)}

				<Column
					sortable
					header={onRenderCompetitorSelectHeader}
					className='otherBrandsCell'
					headerClassName={
						styles.otherBrandsColumnHeader +
						' ' +
						'manual-input__brand-portfolio-head-cell'
					}
					bodyClassName={'manual-input__brand-portfolio-cell'}
					body={(
						rowData: ProductDetailsMarketShareListingResponseModel
					) => HoverTabModel({
						label: String(rowData.totalBrandSales ?? ''),
						tabs: [
							{
								header: `Brand Portfolio Total Sales: ${rowData.totalBrandSales}`,
								isActive: true,
								data:(
									<ul>
										{rowData.brandPortfolioBrands.map(b => (
											<li>{`${b.key} - ${b.value}`}</li>
										))}
									</ul>
								)
							}
						]
					})}
					field={nameof<ProductDetailsMarketShareListingResponseModel>(
						'totalBrandSales'
					)}
				/>

				<Column
					sortable
					header={
						<>
							<p>&nbsp;</p>
							<p>Competitors</p>
						</>
					}
					className='competitorsCell'
					headerClassName='manual-input__competitors-head-cell'
					bodyClassName={'manual-input__competitors-cell'}
					field={nameof<ProductDetailsMarketShareListingResponseModel>(
						'totalCompetitorsSales'
					)}
					body={(
						rowData: ProductDetailsMarketShareListingResponseModel
					) => HoverTabModel({
						label: String(rowData.totalCompetitorsSales ?? ''),
						tabs: [
							{
								header: `Competitors Total Sales: ${rowData.totalCompetitorsSales}`,
								isActive: true,
								data: (
									<ul>
										{rowData.competitorsSales.map(b => (
											<li>{`${b.key} - ${b.value}`}</li>
										))}
									</ul>
								)
							}
						]
					})
					}
				/>

				{selectedCompetitorColumns}

				<Column
					sortable
					header={
						<>
							<p>&nbsp;</p>
							<p>Market share %</p>
						</>
					}
					className='marketShareCell'
					headerClassName='manual-input__total-market-share-percent-head-cell'
					bodyClassName={
						'manual-input__total-market-share-percent-cell'
					}
					field={nameof<ProductDetailsMarketShareListingResponseModel>(
						'marketSharePercent'
					)}
					body={(
						rowData: ProductDetailsMarketShareListingResponseModel
					) => <p>{rowData.marketSharePercent?.toFixed(0) ?? 0} %</p>}
				/>
			</DataTable>
		</ManualInputProvider>
	);
};

export default ManualInput;
